import axios from 'axios'

const API_BASE = 'https://crm.treasurehome.at:8080/crm'
const API_URL = API_BASE + '/api/typeform/leads/1'
const SLOTS_URL = API_BASE + '/appointments/public/slots'
const BOOKING_URL = API_BASE + '/appointments/public/request'
const FORM_ID = 'landing-page'

function uuid() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = Math.random() * 16 | 0
    return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16)
  })
}

function isoNow() {
  return new Date().toISOString()
}

function buildPayload(formData, locale) {
  const fields = []
  const answers = []
  let fieldIdx = 0

  function addField(id, ref, type, title) {
    fields.push({ id, ref, type, title })
    return fieldIdx++
  }

  function addTextAnswer(fieldId, fieldRef, fieldType, value) {
    answers.push({
      type: 'text',
      field: { id: fieldId, ref: fieldRef, type: fieldType },
      text: value || '',
    })
  }

  function addEmailAnswer(fieldId, fieldRef, value) {
    answers.push({
      type: 'email',
      field: { id: fieldId, ref: fieldRef, type: 'email' },
      email: value || '',
    })
  }

  function addPhoneAnswer(fieldId, fieldRef, value) {
    answers.push({
      type: 'phone_number',
      field: { id: fieldId, ref: fieldRef, type: 'phone_number' },
      phone_number: value || '',
    })
  }

  function addNumberAnswer(fieldId, fieldRef, value) {
    answers.push({
      type: 'number',
      field: { id: fieldId, ref: fieldRef, type: 'number' },
      number: typeof value === 'number' ? value : parseFloat(value) || 0,
    })
  }

  function addBooleanAnswer(fieldId, fieldRef, value) {
    answers.push({
      type: 'boolean',
      field: { id: fieldId, ref: fieldRef, type: 'yes_no' },
      boolean: value === true,
    })
  }

  function addChoiceAnswer(fieldId, fieldRef, label, choices) {
    const choiceObj = { label: label || '' }
    answers.push({
      type: 'choice',
      field: { id: fieldId, ref: fieldRef, type: 'multiple_choice' },
      choice: choiceObj,
    })
    // Attach choices to the field definition
    const fieldDef = fields.find(f => f.id === fieldId)
    if (fieldDef) {
      fieldDef.choices = choices.map((c, i) => ({ id: 'ch_' + i, label: c }))
    }
  }

  // Step 1: Personal Info
  const fnId = 'first_name'
  addField(fnId, fnId, 'short_text', locale === 'de' ? 'Vorname' : 'First Name')
  addTextAnswer(fnId, fnId, 'short_text', formData.firstName)

  const lnId = 'last_name'
  addField(lnId, lnId, 'short_text', locale === 'de' ? 'Nachname' : 'Last Name')
  addTextAnswer(lnId, lnId, 'short_text', formData.lastName)

  const phId = 'phone'
  addField(phId, phId, 'phone_number', locale === 'de' ? 'Telefonnummer' : 'Phone Number')
  addPhoneAnswer(phId, phId, formData.phone)

  const emId = 'email'
  addField(emId, emId, 'email', locale === 'de' ? 'E-Mail-Adresse' : 'Email Address')
  addEmailAnswer(emId, emId, formData.email)

  const coId = 'company'
  addField(coId, coId, 'short_text', locale === 'de' ? 'Unternehmen' : 'Company')
  addTextAnswer(coId, coId, 'short_text', formData.company)

  const poId = 'position'
  addField(poId, poId, 'short_text', locale === 'de' ? 'Position' : 'Position')
  addTextAnswer(poId, poId, 'short_text', formData.position)

  // Step 2: Hotel count
  const hcId = 'hotel_count'
  addField(hcId, hcId, 'number', locale === 'de' ? 'Anzahl Hotels' : 'Number of Hotels')
  addNumberAnswer(hcId, hcId, formData.hotelCount)

  // Step 3: Hotel addresses (dynamic)
  if (formData.hotels) {
    formData.hotels.forEach((hotel, idx) => {
      const n = idx + 1
      const prefix = 'hotel_' + n + '_'

      const stId = prefix + 'street'
      addField(stId, stId, 'short_text', `Hotel ${n} - ${locale === 'de' ? 'Straße' : 'Street'}`)
      addTextAnswer(stId, stId, 'short_text', hotel.street)

      const hnId = prefix + 'house_number'
      addField(hnId, hnId, 'short_text', `Hotel ${n} - ${locale === 'de' ? 'Hausnummer' : 'House Number'}`)
      addTextAnswer(hnId, hnId, 'short_text', hotel.houseNumber)

      const zpId = prefix + 'zip'
      addField(zpId, zpId, 'short_text', `Hotel ${n} - ${locale === 'de' ? 'PLZ' : 'ZIP'}`)
      addTextAnswer(zpId, zpId, 'short_text', hotel.zip)

      const ctId = prefix + 'city'
      addField(ctId, ctId, 'short_text', `Hotel ${n} - ${locale === 'de' ? 'Ort' : 'City'}`)
      addTextAnswer(ctId, ctId, 'short_text', hotel.city)

      const dsId = prefix + 'description'
      addField(dsId, dsId, 'long_text', `Hotel ${n} - ${locale === 'de' ? 'Beschreibung' : 'Description'}`)
      addTextAnswer(dsId, dsId, 'long_text', hotel.description)

      const unId = prefix + 'units'
      addField(unId, unId, 'number', `Hotel ${n} - ${locale === 'de' ? 'Anzahl Units' : 'Number of Units'}`)
      addNumberAnswer(unId, unId, hotel.units)
    })
  }

  // Step 4: Channel manager
  const cmOptions = ['Lodgify', 'Smoobu', 'Beds24', '365Villas', 'Guesty', 'Hostaway', 'Cloudbeds', 'Other', 'None']
  const cmId = 'channel_manager'
  addField(cmId, cmId, 'multiple_choice', locale === 'de' ? 'Channelmanager' : 'Channel Manager')
  addChoiceAnswer(cmId, cmId, formData.channelManager, cmOptions)

  // Step 5-8: Boolean questions
  const boolFields = [
    { id: 'has_reception', field: 'hasReception', de: '24/7 Rezeption', en: '24/7 Reception' },
    { id: 'has_stripe', field: 'hasStripe', de: 'Stripe Account', en: 'Stripe Account' },
    { id: 'has_phone_system', field: 'hasPhoneSystem', de: 'Automatische Telefonanlage', en: 'Automated Phone System' },
    { id: 'wants_kiosk', field: 'wantsKiosk', de: 'THL Self-Service Kiosk', en: 'THL Self-Service Kiosk' },
  ]

  boolFields.forEach(bf => {
    addField(bf.id, bf.id, 'yes_no', locale === 'de' ? bf.de : bf.en)
    addBooleanAnswer(bf.id, bf.id, formData[bf.field])
  })

  // Step 9: preferred slot for the first phone meeting (also stored on the
  // lead itself so it is visible in the lead detail even before acceptance)
  if (formData.meetingSlot && formData.meetingSlot !== 'SKIP') {
    const msId = 'meeting_slot'
    addField(msId, msId, 'short_text',
      locale === 'de' ? 'Gewünschter Termin (Erstgespräch)' : 'Preferred time (first call)')
    addTextAnswer(msId, msId, 'short_text', formData.meetingSlot)
  }

  return {
    event_id: uuid(),
    event_type: 'form_response',
    form_response: {
      form_id: FORM_ID,
      token: uuid(),
      landed_at: isoNow(),
      submitted_at: isoNow(),
      definition: {
        id: FORM_ID,
        title: 'Landing Page',
        fields,
      },
      answers,
    },
  }
}

export async function submit(formData, locale) {
  const payload = buildPayload(formData, locale || 'de')
  const response = await axios.post(API_URL, payload, {
    headers: { 'Content-Type': 'application/json' },
  })

  // The webhook returns the created lead id (objects.KsvId) — use it to file
  // the appointment request against the admin's calendar. A booking failure
  // must never fail the lead submission itself.
  if (formData.meetingSlot && formData.meetingSlot !== 'SKIP') {
    try {
      const leadId =
        response.data && response.data.objects ? response.data.objects.KsvId : null
      await requestAppointment(formData, leadId, locale)
    } catch (e) {
      console.error('Appointment request failed (lead was still saved)', e)
    }
  }

  return response.data
}

/** Free slots for the booking step: [{date: 'yyyy-MM-dd', slots: ['09:00', ...]}] */
export async function fetchSlots() {
  const response = await axios.get(SLOTS_URL)
  return (response.data && response.data.objects && response.data.objects.Days) || []
}

/** File the picked slot as a pending appointment request (admin accepts/declines). */
export function requestAppointment(formData, leadId, locale) {
  return axios.post(BOOKING_URL, {
    name: ((formData.firstName || '') + ' ' + (formData.lastName || '')).trim(),
    email: formData.email,
    phone: formData.phone,
    start: formData.meetingSlot,
    leadId: leadId || null,
    locale: locale || 'de',
    notes: formData.company ? 'Firma: ' + formData.company : null,
    website: '', // honeypot — must stay empty
  }, {
    headers: { 'Content-Type': 'application/json' },
  })
}

export { buildPayload }
